
import { RemoveMessage } from '@/core/helper/validation-rule-helper';
import { Button as DXButton, DataGrid, LoadPanel, Popup, ScrollView, SelectBox, TextArea } from 'devextreme-react';
import { t } from 'i18next';
import { useCallback, useMemo, useRef, useState } from 'react'
import DataGridWrapper from '@/core/wrappers/data-grid-wrapper';
import CustomStore from 'devextreme/data/custom_store';
import { productService } from '@/projects/admin/b2b/product/services/product-service';
import { alert, confirm } from 'devextreme/ui/dialog';
import dxCheckBox, { InitializedEvent, ValueChangedEvent } from 'devextreme/ui/check_box';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { toast } from 'react-toastify';

const accounts = new CustomStore({
    key: 'id',
    load: productService.getProductAccounts,
    byKey: async (key, loadOptions: any) => {
        let returnObject = null;
        if (key) {
            loadOptions.filter = ["Id", "=", key];
            const objectsByID = (await productService.getProductAccounts(loadOptions))
            returnObject = objectsByID.data.length > 0 ? objectsByID.data[0] : null;
        }
        return returnObject
    },

    useDefaultSearch: true,
});

const ProductList = () => {
    const dataGridRef = useRef<DataGrid>(null);
    const accountSelectBoxRef = useRef<SelectBox>(null);
    const commentAreaRef = useRef<TextArea>(null);
    const approveCommentAreaRef = useRef<TextArea>(null);
    const rejectCommentAreaRef = useRef<TextArea>(null);
    const selectionRef = useRef<{
        checkBoxUpdating: boolean,
        selectAllCheckBox: dxCheckBox | null
    }>({
        checkBoxUpdating: false,
        selectAllCheckBox: null
    });
    const [popupVisible, setPopupVisible] = useState(false);
    const [approvePopupVisible, setApprovePopupVisible] = useState(false);
    const [rejectPopupVisible, setRejectPopupVisible] = useState(false);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any>(undefined);
    const [mainStore, setMainStore] = useState<CustomStore | undefined>(undefined);
    const [selectedAccount, setSelectedAccount] = useState<any>(undefined);



    const onCellPrepared = async (e: any) => {

        if (e.rowType === 'data' && e.data.isTemp) {
            e.cellElement.bgColor = "#F0F0F0";

        } else if (e.rowType === 'data' && (e.column.type === "buttons" || e.column.type === "selection") && !isSelectable(e.data)) {

            e.cellElement.innerHTML = "";
        }

    };

    function isSelectable(item: any) {
        return item.isTemp;
    }

    async function isSelectAll(dataGrid: any) {
        if (!dataGrid.getDataSource())
            return false;
        const items = await dataGrid.getDataSource().store().load();
        const selectableItems = items.data.filter(isSelectable);
        const selectedRowKeys = dataGrid.option("selectedRowKeys");
        if (!selectedRowKeys || !selectedRowKeys.length) {
            return false;
        }
        return selectedRowKeys.length >= selectableItems.length ? true : undefined;
    }

    const onEditorPreparing = useCallback(async (e: any) => {
        const dataGrid = e.component;
        if (e.type !== 'selection') return;
        if (e.parentType === 'dataRow' && e.row && !isSelectable(e.row.data))
            e.editorOptions.disabled = true;
        if (e.parentType === "headerRow") {
            e.editorOptions.onInitialized = (e: InitializedEvent) => {
                if (e.component)
                    selectionRef.current.selectAllCheckBox = e.component;
            };
            e.editorOptions.value = await isSelectAll(dataGrid);
            e.editorOptions.onValueChanged = async (e: ValueChangedEvent) => {
                if (!e.event) {
                    if (e.previousValue && selectionRef.current.checkBoxUpdating)
                        e.component.option("value", e.previousValue);
                    return;
                }
                if (await isSelectAll(dataGrid) === e.value)
                    return;
                e.value ? dataGrid.selectAll() : dataGrid.deselectAll();
                e.event.preventDefault();
            }
        }

    }, [])

    const onSelectionChanged = useCallback(async (e: SelectionChangedEvent) => {
        const deselectRowKeys: number[] = [];
        e.selectedRowsData.forEach((item: any) => {
            if (!isSelectable(item))
                deselectRowKeys.push(e.component.keyOf(item));
        });
        if (deselectRowKeys.length) {
            e.component.deselectRows(deselectRowKeys);
        }

        selectionRef.current.checkBoxUpdating = true;
        const selectAllCheckBox = selectionRef.current.selectAllCheckBox;
        selectAllCheckBox?.option("value", await isSelectAll(e.component));
        selectionRef.current.checkBoxUpdating = false;
    }, [])

    const onPopupHiding = useCallback(() => {
        setSelectedProduct(undefined);
        setPopupVisible(false);
        commentAreaRef.current?.instance.option("value", undefined);
        approveCommentAreaRef.current?.instance.option("value", undefined);
        rejectCommentAreaRef.current?.instance.option("value", undefined);
    }, [setSelectedProduct, setPopupVisible]);

    const onApprovePopupHiding = useCallback(() => {
        setApprovePopupVisible(false);
        commentAreaRef.current?.instance.option("value", undefined);
        approveCommentAreaRef.current?.instance.option("value", undefined);
        rejectCommentAreaRef.current?.instance.option("value", undefined);
    }, [setApprovePopupVisible]);

    const onRejectPopupHiding = useCallback(() => {
        setRejectPopupVisible(false);
        commentAreaRef.current?.instance.option("value", undefined);
        approveCommentAreaRef.current?.instance.option("value", undefined);
        rejectCommentAreaRef.current?.instance.option("value", undefined);
    }, [setRejectPopupVisible]);

    const showProductInfo = useCallback((e: any) => {
        setPopupVisible(true);
        setSelectedProduct(e.row.data)
    }, [setPopupVisible, setSelectedProduct, commentAreaRef]);


    const approveProducts = async (ids: any, comment: any) => {
        try {
            setLoadPanelVisible(true);
            await productService.approveProducts({ ids: ids, comment })
            setLoadPanelVisible(false);
            onPopupHiding();
            toast.success(t("saved_changes"));
        } catch (error: any) {
            console.log(error)
            setLoadPanelVisible(false);
            toast.error(error.response.data.message);
        }
        await dataGridRef.current?.instance.deselectAll();
        await accountSelectBoxRef.current?.instance.getDataSource().reload();
        accountSelectBoxRef.current?.instance.option('value', undefined);
        accountSelectBoxRef.current?.instance.option('value', selectedAccount);
    }

    const rejectProducts = async (ids: any, comment: any) => {
        try {
            setLoadPanelVisible(true);
            await productService.rejectProducts({ ids: ids, comment })
            setLoadPanelVisible(false);
            onPopupHiding();
            toast.success(t("saved_changes"));
        } catch (error: any) {
            setLoadPanelVisible(false);
            toast.error(error.response.data.message);
        }
        dataGridRef.current?.instance.deselectAll();
        await accountSelectBoxRef.current?.instance.getDataSource().reload();
        accountSelectBoxRef.current?.instance.option('value', undefined);
        accountSelectBoxRef.current?.instance.option('value', selectedAccount);
    }

    const approveSelectedProducts = async () => {
        try {
            const selectedRows = dataGridRef.current?.instance.getSelectedRowsData()
            if (selectedRows && selectedRows.length > 0) {
                await approveProducts(selectedRows.map((item) => item.id), approveCommentAreaRef.current?.instance.option("value"));
                setApprovePopupVisible(false);
            }
            else {
                alert(t("please_select_at_least_one_record"), t("warning"))
            }
        } catch (error: any) {
            setLoadPanelVisible(false);
            toast.error(error.response.data.message);
        }
    }

    const rejectSelectedProducts = async () => {
        try {
            const selectedRows = dataGridRef.current?.instance.getSelectedRowsData()
            if (selectedRows && selectedRows.length > 0) {
                await rejectProducts(selectedRows.map((item) => item.id), rejectCommentAreaRef.current?.instance.option("value"))
                setRejectPopupVisible(false);
            }
            else {
                alert(t("please_select_at_least_one_record"), t("warning"))
            }
        } catch (error: any) {
            setLoadPanelVisible(false);
            toast.error(error.response.data.message);
        }
    }



    const approveSelectedProductOnPopUp = async () => {
        const result = confirm("<i>" + t("are_you_sure") + "</i>", t("approve_request"));
        result.then(async (dialogResult: any) => {
            if (dialogResult) {
                try {
                    setLoadPanelVisible(true);
                    await approveProducts([selectedProduct.id], commentAreaRef.current?.instance.option("value"))
                    setLoadPanelVisible(false);
                } catch (error) {
                    setLoadPanelVisible(false);
                }
            }
        });

    }

    const rejectSelectedProductOnPopUp = async () => {
        const result = confirm("<i>" + t("are_you_sure") + "</i>", t("reject_request"));
        result.then(async (dialogResult: any) => {
            if (dialogResult) {
                try {
                    setLoadPanelVisible(true);
                    await rejectProducts([selectedProduct.id], commentAreaRef.current?.instance.option("value"))
                    setLoadPanelVisible(false);
                } catch (error) {
                    setLoadPanelVisible(false);
                }
            }
        });

    }

    const onValueChanged = (e: any) => {
        if (e.value) {
            setSelectedAccount(e.value);
            setMainStore(new CustomStore({
                cacheRawData: false,
                key: ['id'],
                load: productService.getProducts,
                onLoading(loadOptions) {
                    if (loadOptions.filter && loadOptions.filter.length > 0) {
                        if (Array.isArray(loadOptions.filter[0])) {
                            loadOptions.filter.push('and');
                            loadOptions.filter.push(
                                ["AccountId", "=", e.value],
                            );
                        } else{
                            loadOptions.filter= [loadOptions.filter]
                            loadOptions.filter.push('and');
                            loadOptions.filter.push(
                                ["AccountId", "=", e.value],
                            );
                        }

                    }
                    else {
                        loadOptions.filter = [["AccountId", "=", e.value]]
                    }
                },

                byKey: async (key, loadOptions: any) => {
                    let returnObject = null;
                    if (key) {
                        loadOptions.filter = ["Id", "=", key];
                        const objectsByID = (await productService.getProducts(loadOptions))
                        returnObject = objectsByID.data.length > 0 ? objectsByID.data[0] : null;
                    }
                    return returnObject
                },
                useDefaultSearch: true
            }));
        }
    }

    const approveSelectedBtnOptions = useMemo(() => ({
        text: t("approve_selected"),
        type: 'default',
        icon: 'check',
        stylingMode: "contained", // Dolgun buton görünümü
        elementAttr: {
            class: "custom-toolbar-button", // Özel CSS için sınıf ekliyoruz
        },
        onClick: () => {
            const selectedRows = dataGridRef.current?.instance.getSelectedRowsData()
            if (selectedRows && selectedRows.length > 0) {
                setApprovePopupVisible(true);
            }
            else {
                alert(t("please_select_at_least_one_record"), t("warning"))
            }
        }
    }), []);

    const rejectSelectedBtnOptions = useMemo(() => ({
        text: t("reject_selected"),
        type: 'normal',
        icon: 'close',
        onClick: () => {
            const selectedRows = dataGridRef.current?.instance.getSelectedRowsData()
            if (selectedRows && selectedRows.length > 0) {
                setRejectPopupVisible(true);
            }
            else {
                alert(t("please_select_at_least_one_record"), t("warning"))
            }
        }
    }), []);

    const selectBoxOptions = {
        ref: { accountSelectBoxRef },
        dataSource: { accounts },
        displayExpr: (item: any) => item ? `${item.name} (${item.awaitingApprovalCount})` : '',
        valueExpr: "id",
        placeholder: t("choose_account"),
        showClearButton: true,
        searchEnabled: true,
        searchMode: 'contains',
        onValueChanged: { onValueChanged },
        width: '300',
        itemRender: (item: any) => (
            <span style={{ fontWeight: item.awaitingApprovalCount > 0 ? 'bold' : 'normal' }}>
                {item.name} ({item.awaitingApprovalCount})
            </span>
        )
    };

    return (
        <>
            <div className='row mt-4 '>
                <div className='col-sm mt-4 mb-4 '>
                    <div className='col-sm justify-content-start' >
                        <SelectBox
                            ref={accountSelectBoxRef}
                            dataSource={accounts}
                            displayExpr={(item) => item ? `${item.name} (${item.awaitingApprovalCount})` : ''}
                            valueExpr="id"
                            placeholder={t("choose_account")}
                            showClearButton={true}
                            searchEnabled={true}
                            searchMode='contains'
                            onValueChanged={onValueChanged}
                            width={300}
                            searchExpr={["name"]}
                            itemRender={(item) => (
                                <span style={{ fontWeight: item.awaitingApprovalCount > 0 ? 'bold' : 'normal' }}>
                                    {item.name} ({item.awaitingApprovalCount})
                                </span>
                            )}
                        />
                    </div>
                </div>
            </div>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
                message={t("saving_please_wait")}

            />
            {/* approve popup */}
            <Popup
                visible={approvePopupVisible}
                onHiding={onApprovePopupHiding}
                showTitle={true}
                width={500}
                height={255}
                position={{
                    of: window,
                    at: 'center',
                    my: 'center'
                }}
                title={t("approve_selected")}
            >
                <TextArea
                    ref={approveCommentAreaRef}
                    height={90}
                    readOnly={false}
                    label={t("comment") + ':'}
                />
                <div className='row mt-4 '>

                    <div className='col mt-2 mb-2 '>
                        <div className='row justify-content-end'>
                            <div className='col-auto'>
                                <DXButton
                                    text={t("yes")}
                                    type='default'
                                    icon='check'
                                    onClick={approveSelectedProducts}
                                />
                            </div>
                            <div className='col-auto'>
                                <DXButton
                                    text={t("no")}
                                    type='normal'
                                    icon='close'
                                    onClick={onApprovePopupHiding}
                                />
                            </div>
                        </div>


                    </div>

                </div>


            </Popup>
            {/* reject popup */}
            <Popup
                visible={rejectPopupVisible}
                onHiding={onRejectPopupHiding}
                showTitle={true}
                width={500}
                height={255}
                position={{
                    of: window,
                    at: 'center',
                    my: 'center'
                }}
                title={t("reject_selected")}
            >
                <TextArea
                    ref={rejectCommentAreaRef}
                    height={90}
                    readOnly={false}
                    label={t("comment") + ':'}
                />
                <div className='row mt-4 '>
                    <div className='col mt-2 mb-2 '>
                        <div className='row justify-content-end'>
                            <div className='col-auto'>
                                <DXButton
                                    text={t("yes")}
                                    type='default'
                                    icon='check'
                                    onClick={rejectSelectedProducts}
                                />
                            </div>
                            <div className='col-auto'>
                                <DXButton
                                    text={t("no")}
                                    type='normal'
                                    icon='close'
                                    onClick={onRejectPopupHiding}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Popup>


            <DataGridWrapper
                props={{ id: 'product-list', ref: dataGridRef, wordWrapEnabled: true }}
                permissonCode='Admin.B2B.Product'
                onCellPrepared={onCellPrepared}
                onEditorPreparing={onEditorPreparing}
                onSelectionChanged={onSelectionChanged}
                datasource={mainStore}
                filterVisible={true}
                headerFilterVisible={true}
                selectionConfig={{ mode: 'multiple', selectAllMode: 'allPages', showCheckBoxesMode: 'always' }}
                editingConfig={{ allowDeleting: false, allowUpdating: false, confirmDelete: false, confirmDeleteMessage: RemoveMessage('Product') }}
                pagerVisible={true}
                enableCustomToolbarContent={true}
                isAddNewBtn={false}
                columns={[
                    { dataField: 'name', caption: t('product_name') },
                    { dataField: 'productCode', caption: t('product_code') },
                    { dataField: 'barcodes', caption: t('barcodes') },
                    { dataField: 'productGroup', caption: t('product_group') },
                    {
                        dataField: 'approvalStatus', caption: t('approval_status')
                    },
                    {
                        dataField: 'isTemp', caption: '', sortOrder: "desc",
                        sortIndex: 0, visible: false
                    },
                    {
                        type: 'buttons',
                        width: 60,
                        buttons: [
                            {
                                icon: 'find',
                                name: 'custom',
                                onClick: showProductInfo,
                            }
                        ]
                    }
                ]}
                customToolbarItems={[
                    {
                        widget: "dxButton",
                        location: "after",
                        options: approveSelectedBtnOptions
                    },
                    {
                        widget: "dxButton",
                        location: "after",
                        options: rejectSelectedBtnOptions
                    },
                    // {
                    //     widget: "dxSelectBox",
                    //     location: "before",
                    //     options: selectBoxOptions
                    // }
                ]}
            />
            {selectedProduct && <Popup
                visible={popupVisible}
                onHiding={onPopupHiding}
                showTitle={true}
                width={900}
                height={520}
                position={{
                    of: window,
                    at: 'center',
                    my: 'center'
                }}
                title={t("product")}
            >
                <ScrollView height={250}>

                    <div className="table-responsive">
                        <table className='table table-bordered table-row-dashed table-row-gray-300 align-middle '>
                            <thead>
                                <tr className='fw-bold text-muted'>

                                    <th className='min-w-150px'></th>
                                    <th className='min-w-140px'>{t("old_value")}</th>
                                    <th className='min-w-120px'>{t("new_value")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        <div className='d-flex align-items-center'>
                                            <div className='d-flex justify-content-start flex-column'>
                                                <span className='text-gray-900 fw-bold  fs-6'>
                                                    {t('product_name')}:
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.name !== selectedProduct.name ? 'badge-light-danger text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.oldValue?.name}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.name !== selectedProduct.name ? 'badge-light-success text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.name}
                                        </span>
                                    </td>

                                </tr>
                                <tr>
                                    <td>
                                        <div className='d-flex align-items-center'>
                                            <div className='d-flex justify-content-start flex-column'>
                                                <span className='text-gray-900 fw-bold  fs-6'>
                                                    {t('product_code')}:
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.productCode !== selectedProduct.productCode ? 'badge-light-danger text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.oldValue?.productCode}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.productCode !== selectedProduct.productCode ? 'badge-light-success text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.productCode}
                                        </span>
                                    </td>

                                </tr>
                                <tr>
                                    <td>
                                        <div className='d-flex align-items-center'>
                                            <div className='d-flex justify-content-start flex-column'>
                                                <span className='text-gray-900 fw-bold  fs-6'>
                                                    {t('barcodes')}:
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.barcodes !== selectedProduct.barcodes ? 'badge-light-danger text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.oldValue?.barcodes}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.barcodes !== selectedProduct.barcodes ? 'badge-light-success text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.barcodes}
                                        </span>
                                    </td>

                                </tr>
                                <tr>
                                    <td>
                                        <div className='d-flex align-items-center'>
                                            <div className='d-flex justify-content-start flex-column'>
                                                <span className='text-gray-900 fw-bold  fs-6'>
                                                    {t('product_group')}:
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.productGroup !== selectedProduct.productGroup ? 'badge-light-danger text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.oldValue?.productGroup}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.productGroup !== selectedProduct.productGroup ? 'badge-light-success text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.productGroup}
                                        </span>
                                    </td>

                                </tr>
                                <tr>
                                    <td>
                                        <div className='d-flex align-items-center'>
                                            <div className='d-flex justify-content-start flex-column'>
                                                <span className='text-gray-900 fw-bold  fs-6'>
                                                    {t('measure_unit')}:
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.productMeasureUnit !== selectedProduct.productMeasureUnit ? 'badge-light-danger text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.oldValue?.productMeasureUnit}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.productMeasureUnit !== selectedProduct.productMeasureUnit ? 'badge-light-success text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.productMeasureUnit}
                                        </span>
                                    </td>

                                </tr>
                                <tr>
                                    <td>
                                        <div className='d-flex align-items-center'>
                                            <div className='d-flex justify-content-start flex-column'>
                                                <span className='text-gray-900 fw-bold  fs-6'>
                                                    {t('brand')}:
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.productBrand !== selectedProduct.productBrand ? 'badge-light-danger text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.oldValue?.productBrand}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.productBrand !== selectedProduct.productBrand ? 'badge-light-success text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.productBrand}
                                        </span>
                                    </td>

                                </tr>
                                <tr>
                                    <td>
                                        <div className='d-flex align-items-center'>
                                            <div className='d-flex justify-content-start flex-column'>
                                                <span className='text-gray-900 fw-bold  fs-6'>
                                                    {t('main_product')}:
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.mainProduct !== selectedProduct.mainProduct ? 'badge-light-danger text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.oldValue?.mainProduct}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.mainProduct !== selectedProduct.mainProduct ? 'badge-light-success text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.mainProduct}
                                        </span>
                                    </td>

                                </tr>
                                <tr>
                                    <td>
                                        <div className='d-flex align-items-center'>
                                            <div className='d-flex justify-content-start flex-column'>
                                                <span className='text-gray-900 fw-bold  fs-6'>
                                                    {t('status')}:
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.isActive !== selectedProduct.isActive ? 'badge-light-danger text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.oldValue?.isActive.toString()}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.isActive !== selectedProduct.isActive ? 'badge-light-success text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.isActive.toString()}
                                        </span>
                                    </td>

                                </tr>
                                <tr>
                                    <td>
                                        <div className='d-flex align-items-center'>
                                            <div className='d-flex justify-content-start flex-column'>
                                                <span className='text-gray-900 fw-bold  fs-6'>
                                                    {t('is_stock_stored')}:
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.isStockStored !== selectedProduct.isStockStored ? 'badge-light-danger text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.oldValue?.isStockStored}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.isStockStored !== selectedProduct.isStockStored ? 'badge-light-success text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.isStockStored}
                                        </span>
                                    </td>

                                </tr>
                                <tr>
                                    <td>
                                        <div className='d-flex align-items-center'>
                                            <div className='d-flex justify-content-start flex-column'>
                                                <span className='text-gray-900 fw-bold  fs-6'>
                                                    {t('shelf_life_days')}:
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.shelfLifeDays !== selectedProduct.shelfLifeDays ? 'badge-light-danger text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.oldValue?.shelfLifeDays}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={selectedProduct.oldValue?.shelfLifeDays !== selectedProduct.shelfLifeDays ? 'badge-light-success text-gray-900 fs-6' : 'text-gray-900 fs-6'}>
                                            {selectedProduct.shelfLifeDays}
                                        </span>
                                    </td>

                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ScrollView>


                <TextArea
                    ref={commentAreaRef}
                    height={90}
                    readOnly={false}
                    defaultValue={selectedProduct.comment}
                    label={t("comment") + ':'}
                />
                <div className='row mt-4 '>

                    <div className='col mt-4 mb-4 '>
                        <div className='col-auto justify-content-start' >
                            <DXButton
                                text={t("back")}
                                type='normal'
                                icon='back'
                                onClick={onPopupHiding}
                            />
                        </div>
                    </div>
                    <div className='col mt-4 mb-4 '>
                        <div className='row justify-content-end'>
                            <div className='col-auto'>
                                <DXButton
                                    text={t("approve")}
                                    type='default'
                                    icon='check'
                                    onClick={approveSelectedProductOnPopUp}
                                />
                            </div>
                            <div className='col-auto'>
                                <DXButton
                                    text={t("reject")}
                                    type='normal'
                                    icon='close'
                                    onClick={rejectSelectedProductOnPopUp}
                                />
                            </div>
                        </div>


                    </div>

                </div>


            </Popup>}
        </>
    )
}
export default ProductList